import React, { useRef } from 'react'
import ArcadeLayout from '@/features/arcade/components/ArcadeLayout'
import ArcadeOverviewHowToPlay from '@/features/arcade/components/overview/ArcadeOverviewHowToPlay'
import ArcadeOverviewScarcity from '@/features/arcade/components/overview/ArcadeOverviewScarcity'
import ArcadeOverviewPointValues from '@/features/arcade/components/overview/ArcadeOverviewPointValues/ArcadeOverviewPointValues'
import ArcadePrizeWallHero from '@/features/arcade/components/prizewall/ArcadePrizeWallHero'
import { ArcadeImportantDatesTimeline } from '@/features/arcade/components/ArcadeImportantDatesTimeline'
import useElementDimensions from '@/hooks/useElementDimensions'
import { ButtonLink } from '@/components/Button'
import { buildOgImageUrl } from '@/util/cloudinaryHelpers'
import { OG_TAGS } from '@/constants'
import ArcadeOverviewHero from '@/features/arcade/components/overview/Hero'
import {
  OVERVIEW_PAGE_SECTION_ID,
  PRIZE_WALL_PATH
} from '@/features/arcade/hooks/routing'
import ArcadeSeasonResultsIn from '@/features/arcade/components/overview/ArcadeSeasonResultsIn'
import { getArcadeMergedSeasonProps } from '@/features/arcade/utils'
import { MergedSeason } from '@/features/arcade/types'

const mediaPath = 'pages/arcade/overview'
const ogImage = buildOgImageUrl(
  `${mediaPath}/${OG_TAGS.arcadeOverview.og.image}`
)

const meta = {
  ...OG_TAGS.arcadeOverview,
  og: { ...OG_TAGS.arcadeOverview.og, image: ogImage }
}

type Props = {
  active: MergedSeason
  relevant: MergedSeason
  past: MergedSeason
}

const ArcadeOverviewPage = ({ active, relevant, past }: Props) => {
  const timelineRef = useRef<HTMLDivElement>(null)
  const { width } = useElementDimensions(timelineRef)

  return (
    <ArcadeLayout
      seo={meta}
      season={active}
      navSeasonId={!active ? past?.id : null}
    >
      <ArcadeOverviewHero
        season={!!active ? active : relevant}
        active={active}
      />
      <div className="flex [&>:not(:first-child)]:pt-16 flex-col mb-16 overflow-hidden">
        {!active && <ArcadeSeasonResultsIn season={past} />}
        <div id={OVERVIEW_PAGE_SECTION_ID.HOW_TO_PLAY}>
          <ArcadeOverviewHowToPlay />
        </div>
        <ArcadeOverviewScarcity />
        <div id={OVERVIEW_PAGE_SECTION_ID.POINT_VALUES}>
          <ArcadeOverviewPointValues
            season={relevant}
            offSeason={!!active === false}
          />
        </div>
        <ArcadePrizeWallHero
          title="The Prize Wall"
          description="Mind your rank! Along with the Prize Wall, there will be
              sur-prizes along the way."
          season={!!active ? active : past}
          titleClassName="!h3 md:!h3"
          subTitleClassName="!h4 md:!h4"
          // className="!pt-0"
          buttonSlot={
            <ButtonLink
              size="lg"
              theme="green-arcade"
              href={PRIZE_WALL_PATH}
              className="w-max mt-1 self-center md:self-start"
            >
              View All Prizes
            </ButtonLink>
          }
        />
        <div id={OVERVIEW_PAGE_SECTION_ID.TIMELINE} className="container">
          <ArcadeImportantDatesTimeline
            ref={timelineRef}
            season={relevant}
            config={{
              eventLocation: 'overview',
              isVertical: width < 685,
              textAlign: 'center',
              buttonTheme: 'clean-arcade',
              states: {
                isLaunched: true,
                isRedemptionOpen: false,
                isPostRedemption: false,
                isShipping: false,
                isPostShipping: false
              }
            }}
          />
        </div>
      </div>
    </ArcadeLayout>
  )
}

export default ArcadeOverviewPage

export async function getServerSideProps() {
  const seasons = await getArcadeMergedSeasonProps()

  return {
    props: {
      active: seasons.active,
      relevant: seasons.relevant,
      past: seasons.past
    }
  }
}
